import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, Image as ImageIcon, Search, CheckCircle2, Loader2, Eye, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { usePublicGalleryDownload } from '@/hooks/usePublicGalleryDownload';
import { toast } from 'sonner';
import { Dialog, DialogContent } from '@/components/ui/dialog';

interface PublicGalleryImage {
  id: string;
  prompt: string;
  image_url: string;
  model: string;
  size: string;
  quality: string;
  style?: string;
  created_at: string;
  display_category: string;
  download_count: number;
  featured: boolean;
  is_enhanced: boolean;
  enhanced_image_url?: string;
}

const CATEGORIES = [
  'All',
  'Photography & Realism',
  'Digital Art',
  '3D & CGI',
  'Fantasy & Sci-Fi',
  'Minimalist & Abstract',
  'Architecture & Interior',
  'Nature & Landscape',
  'Uncategorized'
];

const PublicGallery = () => {
  const [images, setImages] = useState<PublicGalleryImage[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [selectedImage, setSelectedImage] = useState<PublicGalleryImage | null>(null);
  
  const { downloadImagesAsZip, downloadSingleImage } = usePublicGalleryDownload();
  
  const PAGE_SIZE = 12;

  const fetchImages = async (reset = false) => {
    if (reset) {
      setLoading(true);
      setOffset(0);
    } else {
      setLoadingMore(true);
    }

    try {
      const category = selectedCategory === 'All' ? null : selectedCategory;
      const newOffset = reset ? 0 : offset;

      const rangeStart = newOffset;
      const rangeEnd = newOffset + PAGE_SIZE - 1;

      let query = supabase
        .from('public_gallery_images')
        .select('*');

      if (category) {
        query = query.eq('display_category', category);
      }

      if (searchTerm.trim()) {
        query = query.ilike('prompt', `%${searchTerm}%`);
      }

      if (sortBy === 'popular') {
        query = query.order('download_count', { ascending: false }).order('created_at', { ascending: false });
      } else {
        query = query.order('created_at', { ascending: false });
      }

      query = query.range(rangeStart, rangeEnd);

      const { data, error } = await query;

      if (error) throw error;

      if (data) {
        if (reset) {
          setImages(data as unknown as PublicGalleryImage[]);
        } else {
          setImages(prev => [...prev, ...(data as unknown as PublicGalleryImage[])]);
        }
        setHasMore((data as any[]).length === PAGE_SIZE);
        setOffset(newOffset + PAGE_SIZE);
      }
    } catch (error) {
      console.error('Error fetching gallery images:', error);
      toast.error('Failed to load gallery images');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchImages(true);
  }, [selectedCategory]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchImages(true);
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchTerm, sortBy]);

  const filteredImages = images.filter(img =>
    img.prompt.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedImages = [...filteredImages].sort((a, b) => {
    if (sortBy === 'newest') {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    } else if (sortBy === 'popular') {
      return b.download_count - a.download_count;
    }
    return 0;
  });

  const toggleImageSelection = (imageId: string) => {
    setSelectedImages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(imageId)) {
        newSet.delete(imageId);
      } else {
        newSet.add(imageId);
      }
      return newSet;
    });
  };

  const handleDownloadSelected = () => {
    const selected = sortedImages.filter(img => selectedImages.has(img.id));
    if (selected.length === 0) {
      toast.error('Please select images to download');
      return;
    }
    downloadImagesAsZip(selected, selectedCategory);
  };

  const handleDownloadAll = () => {
    downloadImagesAsZip(sortedImages, selectedCategory);
  };

  return (
    <section className="py-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
            Community Gallery
          </h2>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Explore thousands of AI-generated images from our community
          </p>
          <div className="mt-6 flex items-center justify-center gap-4 text-sm text-gray-400">
            <span className="flex items-center gap-2">
              <ImageIcon className="w-4 h-4" />
              {filteredImages.length} images
            </span>
            <span>•</span>
            <span>{CATEGORIES.length - 1} categories</span>
          </div>
        </div>

        {/* Controls */}
        <div className="mb-8 space-y-4">
          {/* Search and Sort */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                placeholder="Search by prompt..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
              />
            </div>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full md:w-48 bg-white/10 border-white/20 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest First</SelectItem>
                <SelectItem value="popular">Most Downloaded</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Category Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {CATEGORIES.map(category => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                onClick={() => setSelectedCategory(category)}
                className={`whitespace-nowrap ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
                    : 'bg-white/10 border-white/20 text-white hover:bg-white/20'
                }`}
              >
                {category}
              </Button>
            ))}
          </div>

          {/* Bulk Actions */}
          {selectedImages.size > 0 && (
            <Card className="p-4 bg-blue-500/20 border-blue-500/30">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <span className="text-white font-medium">
                  {selectedImages.size} images selected
                </span>
                <div className="flex gap-2">
                  <Button
                    onClick={handleDownloadSelected}
                    className="bg-gradient-to-r from-blue-500 to-purple-500"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Selected
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setSelectedImages(new Set())}
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    Clear Selection
                  </Button>
                </div>
              </div>
            </Card>
          )}

          <div className="flex justify-between items-center">
            <Button
              variant="outline"
              onClick={() => {
                const allIds = new Set(sortedImages.map(img => img.id));
                setSelectedImages(allIds);
              }}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Select All ({sortedImages.length})
            </Button>
            <Button
              onClick={handleDownloadAll}
              className="bg-gradient-to-r from-green-500 to-blue-500"
            >
              <Download className="w-4 h-4 mr-2" />
              Download All in {selectedCategory}
            </Button>
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-20">
            <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-blue-400" />
            <p className="text-gray-400">Loading gallery images...</p>
          </div>
        ) : sortedImages.length === 0 ? (
          <div className="text-center py-20">
            <ImageIcon className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-400">No images found in this category</p>
          </div>
        ) : (
          <>
            {/* Image Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
              {sortedImages.map((image, idx) => (
                <Card
                  key={image.id}
                  className="group relative overflow-hidden bg-white/5 border-white/10 hover:border-purple-500/50 transition-all duration-300"
                >
                  <div className="aspect-square relative">
                    <img
                      src={image.image_url}
                      alt={image.prompt}
                      loading="lazy"
                      decoding="async"
                      fetchPriority={idx < 8 ? 'high' : 'low'}
                      className="w-full h-full object-cover"
                    />
                    
                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 p-4 flex flex-col justify-between">
                      <div className="flex justify-between items-start">
                        <Badge className="bg-purple-500/90">
                          {image.display_category || 'Uncategorized'}
                        </Badge>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => toggleImageSelection(image.id)}
                          className={`${
                            selectedImages.has(image.id)
                              ? 'bg-blue-500 text-white'
                              : 'bg-white/20 text-white'
                          }`}
                        >
                          <CheckCircle2 className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      <div>
                        <p className="text-white text-sm mb-3 line-clamp-2">
                          {image.prompt}
                        </p>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => setSelectedImage(image)}
                            className="flex-1 bg-white/20 hover:bg-white/30"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => downloadSingleImage(
                              image.image_url,
                              image.prompt,
                              image.id
                            )}
                            className="flex-1 bg-gradient-to-r from-blue-500 to-purple-500"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Download
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Load More */}
            {hasMore && (
              <div className="text-center">
                <Button
                  onClick={() => fetchImages(false)}
                  disabled={loadingMore}
                  className="bg-gradient-to-r from-blue-500 to-purple-500"
                >
                  {loadingMore ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    'Load More Images'
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Image Preview Modal */}
      <Dialog open={!!selectedImage} onOpenChange={(open) => !open && setSelectedImage(null)}>
        <DialogContent className="max-w-4xl bg-slate-900 border-white/20">
          {selectedImage && (
            <div className="space-y-4">
              <img
                src={selectedImage.image_url}
                alt={selectedImage.prompt}
                className="w-full rounded-lg"
              />
              <div className="space-y-2">
                <p className="text-white font-medium">{selectedImage.prompt}</p>
                <div className="flex gap-2 flex-wrap">
                  <Badge>{selectedImage.model}</Badge>
                  <Badge variant="outline">{selectedImage.size}</Badge>
                  <Badge variant="outline">{selectedImage.quality}</Badge>
                </div>
                <Button
                  onClick={() => downloadSingleImage(
                    selectedImage.image_url,
                    selectedImage.prompt,
                    selectedImage.id
                  )}
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-500"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Image
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default PublicGallery;
